import type { ReactNode } from 'react';
import { Button, DataTable, EmptyState, Icon, Panel, Tag, type Column } from '@dayon/design-system';
import type { StaffActivityRow } from '@dayon/contracts';
import {
  activityHeadline,
  deviceWords,
  detailWords,
  outcomeWords,
  retentionWords,
} from '../activity-log.ts';

/**
 * The player's log as staff read it: props in, markup out, no hooks, so it renders the same on a
 * server as in a browser and is tested by rendering.
 *
 * A FAILED READ is its own state and never reads as an empty one: "nothing is listed" and "the
 * player did nothing" are different facts, and an investigator acts on the second.
 */
export interface ActivityLogTableProps {
  rows: StaffActivityRow[];
  loading: boolean;
  failed: boolean;
  hasMore: boolean;
  canExport: boolean;
  /** The period the platform keeps addresses for, from the read; null until the first answer. */
  retentionDays: number | null;
  /** Manila time: the filters are Manila days and the file is in Manila time. */
  formatMoment: (iso: string) => string;
  onMore: () => void;
  onExport: () => void;
  /** The filter controls, drawn between the title and the explanation. */
  filters?: ReactNode;
  /** What the last export produced, drawn above the list. */
  exportResult?: ReactNode;
  /** True while the export is being made. */
  exporting?: boolean;
}

export function ActivityLogTable({
  rows,
  loading,
  failed,
  hasMore,
  canExport,
  retentionDays,
  formatMoment,
  onMore,
  onExport,
  filters,
  exportResult,
  exporting = false,
}: ActivityLogTableProps) {
  const columns: Column<StaffActivityRow>[] = [
    {
      key: 'when',
      header: 'When',
      render: (r) => <span className="tabular">{formatMoment(r.at)}</span>,
    },
    {
      key: 'what',
      header: 'What happened',
      required: true,
      render: (r) => activityHeadline(r.kind, r.outcome),
    },
    {
      key: 'outcome',
      header: 'Outcome',
      render: (r) => (
        <Tag tone={r.outcome === 'SUCCEEDED' ? 'neutral' : 'outline'}>
          {outcomeWords(r.outcome)}
        </Tag>
      ),
    },
    {
      key: 'ip',
      header: 'IP',
      render: (r) => (r.ip ? <span className="tabular">{r.ip}</span> : '—'),
    },
    {
      key: 'device',
      header: 'Device',
      render: (r) => {
        const words = deviceWords(r);
        return words === '—' ? (
          words
        ) : (
          <span className="cell-sub" title={r.userAgent ?? undefined}>
            {words}
          </span>
        );
      },
    },
    {
      key: 'details',
      header: 'Details',
      render: (r) => {
        const lines = detailWords(r);
        return lines.length === 0 ? (
          '—'
        ) : (
          <>
            {lines.map((line, i) => (
              <div key={i} className="cell-sub">
                {line}
              </div>
            ))}
          </>
        );
      },
    },
  ];

  return (
    <Panel
      title="Activity log · newest first"
      actions={
        canExport ? (
          <Button small onClick={onExport} disabled={exporting}>
            <Icon name="download-simple" /> Export log
          </Button>
        ) : undefined
      }
    >
      <div className="stack">
        {filters}
        <p className="text-muted" style={{ margin: 0 }}>
          Sign-ins, failed sign-ins and every change the player made, with the network address and
          device each came from. Viewing the account is never recorded here. Addresses and browser
          details are removed after{' '}
          {retentionDays === null ? 'the retention period' : retentionWords(retentionDays)}; the
          entry stays. Times are Manila time.
        </p>
        {!canExport && (
          <div className="notice notice-quiet">
            <Icon name="info" />
            <div>
              Exporting this log needs audit.export, which your role does not hold. An administrator
              who manages roles can grant it.
            </div>
          </div>
        )}
        {exportResult}
        {failed ? (
          <EmptyState icon="warning-circle" title="The log could not be read">
            The platform did not answer, so nothing is listed. That is not the same as the player
            having done nothing — try again shortly.
          </EmptyState>
        ) : loading && rows.length === 0 ? (
          <div className="skeleton" style={{ height: '240px' }}>
            &nbsp;
          </div>
        ) : (
          <DataTable
            columns={columns}
            rows={rows}
            rowKey={(r) => r.id}
            caption="Everything the player did, newest first"
            empty={
              <EmptyState icon="clock-counter-clockwise" title="Nothing recorded in this range">
                Activity is recorded from the day this log was switched on. Earlier sign-ins appear
                on the Security tab as sessions.
              </EmptyState>
            }
          />
        )}
        {hasMore && !failed && (
          <div>
            <Button small onClick={onMore} disabled={loading}>
              Show older
            </Button>
          </div>
        )}
      </div>
    </Panel>
  );
}
